import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import shallow from 'zustand/shallow';

import { getProductVoucherEligibilityList } from '../queries';
import { useAuthStore, useShipToStore } from '../stores';

export const PRODUCT_VOUCHER_ELIGIBILITY_LIST_KEY =
  'ProductVoucherEligibilityList';

interface UseProductVoucherEligibilityListParams {
  itemCode?: number;
  size?: number;
  enabled?: boolean;
}

export const useProductVoucherEligibilityList = ({
  itemCode,
  size = 20,
  enabled = true,
}: UseProductVoucherEligibilityListParams) => {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouse = useShipToStore((state) => state.selectedShipToId, shallow);

  const normalizedItemCode = useMemo(() => Number(itemCode ?? 0), [itemCode]);

  return useInfiniteQuery({
    queryKey: [
      PRODUCT_VOUCHER_ELIGIBILITY_LIST_KEY,
      warehouse,
      normalizedItemCode,
      size,
    ],
    queryFn: async ({ pageParam }) => {
      if (!accessToken || !warehouse) {
        throw new Error('Missing auth token or warehouse');
      }

      return getProductVoucherEligibilityList(accessToken, {
        item_code: normalizedItemCode,
        warehouse,
        page: Number(pageParam),
        size,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      return nextPage <= lastPage.pagination.pages ? nextPage : undefined;
    },
    enabled:
      enabled &&
      Boolean(accessToken) &&
      Boolean(warehouse) &&
      normalizedItemCode > 0,
    staleTime: 60 * 1000,
    retry: 1,
  });
};
